import React from 'react';
import { ChevronDownIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';

interface SortOption {
  value: string;
  label: string;
  description?: string;
}

interface SortDropdownProps {
  value: string;
  onChange: (sortBy: string, sortDirection: string) => void;
  options: SortOption[];
  sortDirection?: string;
  className?: string;
  iconOnly?: boolean;
}

const SortDropdown: React.FC<SortDropdownProps> = ({
  value,
  onChange,
  options,
  sortDirection = 'desc',
  className = '',
  iconOnly = false
}) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  
  const currentOption = options.find(opt => opt.value === value) || options[0];
  
  const handleSortChange = (newSortBy: string) => {
    // If clicking the same sort option, toggle direction
    if (newSortBy === value) {
      const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
      onChange(newSortBy, newDirection);
    } else {
      // New sort option, use default desc direction
      onChange(newSortBy, 'desc');
    }
    setIsOpen(false);
  };

  const getSortIcon = (isCurrentSort: boolean) => {
    if (!isCurrentSort) return null;

    return (
      <span className={iconOnly ? 'text-lg font-bold' : 'ml-1 text-xs'}>
        {sortDirection === 'desc' ? '↓' : '↑'}
      </span>
    );
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center ${iconOnly ? 'gap-0' : 'gap-1 sm:gap-2'} ${iconOnly ? 'w-8 h-8' : 'px-2 sm:px-3 py-1.5'} text-xs sm:text-sm font-medium rounded-md border transition-colors whitespace-nowrap`}
        style={{
          backgroundColor: iconOnly ? theme.primary + '15' : theme.components.card.background,
          color: iconOnly ? theme.primary : theme.text,
          borderColor: iconOnly ? theme.primary + '50' : theme.components.card.border
        }}
        title={iconOnly ? `Sort: ${currentOption.label} (${sortDirection === 'desc' ? 'Descending' : 'Ascending'})` : undefined}
      >
        <ArrowsUpDownIcon className="w-4 h-4" />
        {!iconOnly && (
          <>
            <span className="hidden sm:inline">Sort: {currentOption.label}</span>
            <span className="sm:hidden">{currentOption.label}</span>
          </>
        )}
        {getSortIcon(!iconOnly)}
        {!iconOnly && <ChevronDownIcon className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown - responsive width and positioning */}
          <div
            className="absolute right-0 mt-1 w-screen max-w-xs sm:w-64 rounded-md shadow-lg border z-20 max-h-[70vh] overflow-y-auto"
            style={{
              backgroundColor: theme.components.card.background,
              borderColor: theme.components.card.border
            }}
          >
            <div className="py-1">
              {options.map((option) => {
                const isCurrentSort = option.value === value;

                return (
                  <button
                    key={option.value}
                    onClick={() => handleSortChange(option.value)}
                    className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-2 text-sm transition-colors hover:bg-opacity-50 active:bg-opacity-75"
                    style={{
                      color: isCurrentSort ? theme.primary : theme.text,
                      backgroundColor: isCurrentSort ? theme.primary + '15' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrentSort) {
                        e.currentTarget.style.backgroundColor = theme.gray[100] || '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrentSort) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-1">
                          {option.label}
                          {getSortIcon(isCurrentSort)}
                        </div>
                        {option.description && (
                          <div className="text-xs opacity-75 mt-0.5">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SortDropdown;